import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageCircle, Star, Camera, Grid3x3, Layers, Zap } from 'lucide-react';

interface FeedbackRating {
  accuracy: number;
  completeness: number;
  textureQuality: number;
  performance: number;
}

interface Props {
  onSubmit: (feedback: {
    rating: FeedbackRating;
    issues: string[];
    comment: string;
  }) => void;
  onClose: () => void;
}

export default function ReconstructionFeedback({ onSubmit, onClose }: Props) {
  const [rating, setRating] = useState<FeedbackRating>({
    accuracy: 0,
    completeness: 0,
    textureQuality: 0,
    performance: 0
  });

  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  const ratingCriteria = [
    { id: 'accuracy', label: 'Geometric Accuracy', icon: Grid3x3, description: 'How accurately does the 3D model match the real object?' },
    { id: 'completeness', label: 'Model Completeness', icon: Layers, description: 'Are all parts of the object reconstructed?' },
    { id: 'textureQuality', label: 'Texture Quality', icon: Camera, description: 'How clear and accurate are the surface textures?' },
    { id: 'performance', label: 'Performance', icon: Zap, description: 'How well does the 3D model perform in the viewer?' }
  ];

  const commonIssues = [
    'Missing parts or holes in the model',
    'Blurry or low-quality textures',
    'Incorrect geometry or distortions',
    'Poor alignment between photos',
    'Slow loading or viewer performance',
    'Incorrect scale or proportions',
    'Color inconsistencies',
    'Artifacts or floating geometry'
  ];

  const handleRatingChange = (criteria: keyof FeedbackRating, value: number) => {
    setRating(prev => ({ ...prev, [criteria]: value }));
  };

  const handleIssueToggle = (issue: string) => {
    setSelectedIssues(prev =>
      prev.includes(issue)
        ? prev.filter(i => i !== issue)
        : [...prev, issue]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      rating,
      issues: selectedIssues,
      comment
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-[1000]">
      <div className="bg-[#161513] rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#0CB4E4]/30">
        <div className="p-6 border-b border-[#0CB4E4]/20">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-[#0CB4E4]" />
            <h2 className="text-2xl font-bold text-white">Rate Reconstruction Quality</h2>
          </div>
          <p className="mt-2 text-gray-300">
            Help us improve our 3D reconstruction by providing feedback on the results.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Rating Section */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4">Quality Ratings</h3>
              <div className="space-y-4">
                {ratingCriteria.map(({ id, label, icon: Icon, description }) => (
                  <div key={id} className="p-4 bg-[#161513] rounded-lg border border-[#0CB4E4]/30">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="w-5 h-5 text-[#0CB4E4]" />
                      <div>
                        <div className="font-medium text-white">{label}</div>
                        <div className="text-sm text-gray-300">{description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleRatingChange(id as keyof FeedbackRating, value)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            rating[id as keyof FeedbackRating] >= value
                              ? 'bg-[#0CB4E4] text-[#161513]'
                              : 'bg-[#161513] border border-[#0CB4E4]/30 text-gray-300 hover:bg-[#0CB4E4]/20'
                          }`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Issues Section */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4">Common Issues</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {commonIssues.map(issue => (
                  <button
                    key={issue}
                    type="button"
                    onClick={() => handleIssueToggle(issue)}
                    className={`p-3 rounded-lg text-left transition-all ${
                      selectedIssues.includes(issue)
                        ? 'bg-[#0CB4E4]/20 border-2 border-[#0CB4E4] text-white'
                        : 'bg-[#161513] border-2 border-[#0CB4E4]/30 text-gray-300 hover:border-[#0CB4E4]/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {selectedIssues.includes(issue) ? (
                        <ThumbsDown className="w-4 h-4 text-[#0CB4E4]" />
                      ) : (
                        <ThumbsDown className="w-4 h-4 text-gray-300" />
                      )}
                      <span className="text-sm">{issue}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Comments */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4">Additional Comments</h3>
              <div className="relative">
                <MessageCircle className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share any other feedback about the reconstruction..."
                  rows={4}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] resize-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white font-medium rounded-lg hover:bg-[#161513] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#0CB4E4] text-[#161513] font-medium rounded-lg hover:bg-[#0CB4E4]/90 transition-colors shadow-md"
            >
              Submit Feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
